import React from "react";
import ExcelJS from "exceljs";
import { getFileStatus } from "../../utils/fileStatus";
import { parseCustomDate } from "./ExcelVettingReport";
import moment from "moment";

const ExcelRectificationReport = ({ data }) => {
    const exportExcelFile = async () => {
        if (!Array.isArray(data)) {
            return;
        }
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        // Define columns with headers
        worksheet.columns = [
            { header: 'S.No', key: 'serial_no', width: 10 },
            { header: 'File No', key: 'file_no', width: 20 },
            { header: 'Law Hands Branch', key: 'lh_branch', width: 20 },
            { header: 'Bank Name', key: 'bank_name', width: 25 },
            { header: 'Product', key: 'product_name', width: 15 },
            { header: 'Bank Branch Name', key: 'branch_name', width: 25 },
            { header: 'Borrower Name', key: 'borrower_name', width: 25 },
            { header: 'Property Ownername', key: 'property_owner', width: 25 },
            { header: 'Process Officer', key: 'po_name', width: 25 },
            { header: 'Verification Officer', key: 'vo_name', width: 25 },
            { header: 'Double Verification Officer', key: 'dvo_name', width: 25 },
            { header: 'Rectification Login Date and Time', key: 'created_dt', width: 20 },
            { header: 'Rectification Received Date and Time', key: 'arrived_dt', width: 20 },
            { header: 'File Rectification Grade', key: 'rectify_file_grade', width: 20 },
            { header: 'File Picked By', key: 'picked_by', width: 20 },
            { header: 'File Initial Status', key: 'initial_status', width: 15 },
            { header: 'File Completed Status', key: 'completed_status', width: 20 },
            { header: 'File Picked Date & Time', key: 'picked_dt', width: 20 },
            { header: 'File Completed Date & Time', key: 'completed_dt', width: 25 },
            { header: 'File Queue Time', key: 'waiting_time', width: 15 },
            { header: 'File Working Time', key: 'working_time', width: 15 },
            { header: 'Total Time', key: 'total_time', width: 15 },
            { header: 'Mistakes Made by', key: 'mistake_made_by', width: 20 },
            { header: 'Error Type', key: 'error_category', width: 20 },
            { header: 'Place of Error Category', key: 'rectify_category', width: 20 },
            { header: 'Sub Category', key: 'rectify_sub_category', width: 20 },
            { header: `What's Wrong`, key: 'whatWrong', width: 30 },
            { header: 'What to do Change', key: 'whatChange', width: 30 },
            { header: 'Reason', key: 'reason', width: 30 },
        ];

        // Apply alignment and color to header cells
        worksheet.getRow(1).eachCell((cell) => {
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFF00' },
            };
            cell.font = { bold: true, size: 14 };
        });

        const flattenedData = data.map((report, index) => {
            // Parse dates only if they exist and are valid
            const arrived = report.arrived_dt ? moment(report.arrived_dt, "DD-MM-YYYY HH:mm:ss") : null;
            const picked = report.picked_dt ? moment(report.picked_dt, "DD-MM-YYYY HH:mm:ss") : null;
            const completed = report.completed_dt ? moment(report.completed_dt, "DD-MM-YYYY HH:mm:ss") : null;

            // Calculate differences only if both dates are valid
            const waiting_diff = arrived && picked && arrived.isValid() && picked.isValid()
                ? moment.duration(picked.diff(arrived)).asMilliseconds()
                : null;

            const working_diff = picked && completed && picked.isValid() && completed.isValid()
                ? moment.duration(completed.diff(picked)).asMilliseconds()
                : null;

            const waiting_time = waiting_diff !== null ? waiting_diff / (24 * 60 * 60 * 1000) : null;
            const working_time = working_diff !== null ? working_diff / (24 * 60 * 60 * 1000) : null;

            // ✅ Calculate total_time
            const total_time =
                waiting_time !== null && working_time !== null
                    ? waiting_time + working_time
                    : waiting_time !== null
                        ? waiting_time
                        : working_time !== null
                            ? working_time
                            : null;

            return {
                serial_no: report.serial_no,
                file_no: report.file_no,
                lh_branch: report.lh_branch,
                bank_name: report.bank_name,
                product_name: report.product_name,
                branch_name: report.branch_name,
                borrower_name: report.borrower_name,
                property_owner: report.property_owner,
                po_name: report.po_name,
                vo_name: report.vo_name,
                dvo_name: report.dvo_name,
                created_dt: report.created_dt ? parseCustomDate(report.created_dt) : "",
                arrived_dt: report.arrived_dt ? parseCustomDate(report.arrived_dt) : null,
                picked_dt: report.picked_dt ? parseCustomDate(report.picked_dt) : null,
                completed_dt: report.completed_dt ? parseCustomDate(report.completed_dt) : null,
                rectify_file_grade: report.rectify_file_grade,
                picked_by: report.picked_by,
                initial_status: report.initial_status,
                completed_status: report.completed_status,
                reason: report.reason,
                whatWrong: report.whatWrong,
                whatChange: report.whatChange,
                mistake_made_by: report.mistake_made_by,
                error_category: report.error_category,
                rectify_category: report.rectify_category,
                rectify_sub_category: report.rectify_sub_category,
                waiting_time,
                working_time,
                total_time
            };
        });

        // Add data rows with custom cell alignment and wrap text
        flattenedData.forEach((item) => {
            const row = worksheet.addRow(item);
            row.eachCell((cell, colNumber) => {
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                if ([1].includes(colNumber)) {
                    cell.numFmt = '0'; // Integer format
                } else if ([4].includes(colNumber)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                } else if ([24].includes(colNumber)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
                } else if ([12, 13, 18, 19].includes(colNumber)) {
                    cell.numFmt = 'dd-mm-yyyy hh:mm:ss'; // For date & time format
                } else if ([20, 21, 22].includes(colNumber)) { // Waiting and Working Time columns
                    if (cell.value !== null) {
                        cell.numFmt = '[h]:mm:ss'; // Excel format for time
                    } else {
                        cell.value = ''; // Clear cell if no value
                    }
                }
            });
        });
        const currentDate = new Date();
        const formattedDate = `${currentDate.getDate().toString().padStart(2, '0')}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getFullYear()}`;
        // Generate Excel file buffer
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `Rectification Subreport ${formattedDate}.xlsx`;
        anchor.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <>
            <button className="excel-download-btn" onClick={exportExcelFile}>
                Download as Excel
            </button>
        </>
    );
};

export default ExcelRectificationReport;
